import { getTokens } from "@utils/authStorage";
import { Product } from "./fetchProducts";

// TODO: Configure this RevenueCat token in environment variables
// This should be your RevenueCat Web Purchase token from the RevenueCat dashboard
const REVENUECAT_TOKEN = 
  process.env.NODE_ENV === "production"
    ? "jpgrofoixhhwiqky" // Production token
    : "svewmqjkgapexscz"; // Sandbox token for development/local

export interface RevenueCatPurchaseOptions {
  product: Product;
  successUrl?: string;
  cancelUrl?: string;
  metadata?: Record<string, string>;
}

/**
 * Creates a RevenueCat Web Purchase Link for a given product
 * Format: https://pay.rev.cat/<token>/<userId>?email=<email>&metadata=<data>
 */
export function createRevenueCatPurchaseLink({
  product,
  successUrl,
  cancelUrl,
  metadata = {}
}: RevenueCatPurchaseOptions): string {
  const { userData } = getTokens();
  
  // Debug logging for user data
  console.log("🔍 [RevenueCat Purchase Link] Retrieved userData from storage:", userData);
  console.log("🔍 [RevenueCat Purchase Link] User UUID:", userData?.uuid);
  console.log("🔍 [RevenueCat Purchase Link] User email:", userData?.email);
  
  if (!userData) {
    throw new Error("User must be authenticated to create purchase link");
  }
  
  if (!userData.uuid) {
    throw new Error("User ID (uuid) is required for RevenueCat purchase");
  }
  
  if (!userData.email) {
    throw new Error("User email is required for RevenueCat purchase");
  }
  
  if (!product.revenue_cat_product_id) {
    throw new Error(`Product ${product.display_name} does not have a RevenueCat product ID`);
  }
  
  // Base RevenueCat URL
  const baseUrl = `https://pay.rev.cat/${REVENUECAT_TOKEN}/${userData.uuid}`;
  
  console.log("🔗 [RevenueCat Purchase Link] Generated base URL with UUID:", baseUrl);
  
  // Build URL parameters
  const params = new URLSearchParams();
  
  // Required email parameter
  params.append('email', userData.email);
  
  // Add product ID to purchase
  params.append('product', product.revenue_cat_product_id);
  
  // Add success/cancel URLs if provided
  if (successUrl) {
    params.append('success_url', successUrl);
  }
  
  if (cancelUrl) {
    params.append('cancel_url', cancelUrl);
  }
  
  // Add metadata (product info, tier, etc.)
  const enhancedMetadata = {
    product_tier: product.tier,
    product_name: product.display_name,
    renewable_type: product.renewable_type,
    image_credits: product.image_credits.toString(),
    style_credits: product.style_credits.toString(),
    ...metadata
  };
  
  // Add metadata as individual parameters (RevenueCat format)
  Object.entries(enhancedMetadata).forEach(([key, value]) => {
    params.append(`metadata[${key}]`, value);
  });
  
  const finalUrl = `${baseUrl}?${params.toString()}`;
  console.log("🚀 [RevenueCat Purchase Link] Final purchase URL:", finalUrl);
  console.log("🎯 [RevenueCat Purchase Link] URL contains UUID:", finalUrl.includes(userData.uuid));
  
  return finalUrl;
}

/**
 * Redirects the browser to the RevenueCat purchase page for a given product
 */
export function redirectToRevenueCatPurchase(options: RevenueCatPurchaseOptions): void {
  const purchaseUrl = createRevenueCatPurchaseLink(options);
  console.log("🌐 [RevenueCat Redirect] Redirecting to RevenueCat purchase:", purchaseUrl);
  
  // Log the URL without redirecting for debugging
  console.log("🔗 [DEBUG] Full RevenueCat purchase URL (copy this to test):");
  console.log(purchaseUrl);
  
  // Temporarily comment out the redirect to see the URL
  // window.location.href = purchaseUrl;
  
  // For now, just open in a new tab so you can see the URL in console
  window.open(purchaseUrl, '_blank');
} 